import SEO from '../components/SEO';
import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { BarChart3, Brain, Clock, CheckCircle2, TrendingUp, DollarSign, Users, Target, Zap, ShieldCheck, Sparkles, RefreshCw, FileText, Boxes, Warehouse, Calculator, AlertTriangle, Database, ArrowRight, ChevronDown, ChevronUp, Package, TruckIcon, Lightbulb, Award, Calendar } from 'lucide-react';

const QuickStartPage = () => {
  const [expandedFaq, setExpandedFaq] = useState(null);

  useEffect(() => {
    document.title = 'QuickStart for SAP Business One | BI & Analytics in Weeks';
  }, []);

  const toggleFaq = (index) => {
    setExpandedFaq(expandedFaq === index ? null : index);
  };

  const faqs = [
    {
      question: "What is QuickStart for SAP Business One?",
      answer: "QuickStart is a complete Business Intelligence and planning solution designed specifically for SAP Business One users. It transforms your SAP B1 data into interactive dashboards, financial reports, and forecasting tools-delivering actionable insights in weeks, not months. Built on IBM Cognos Analytics and Planning Analytics, it requires no additional data entry and integrates seamlessly with your existing SAP Business One environment."
    },
    {
      question: "How quickly can QuickStart be implemented for SAP B1?",
      answer: "QuickStart is designed for rapid deployment-most SMEs are live within 2-3 weeks. This fast timeline is possible because QuickStart comes pre-configured with SAP Business One data models, reports, and dashboards. Our proven implementation methodology ensures minimal disruption to your operations while delivering immediate value."
    },
    {
      question: "What modules are included in QuickStart for SAP Business One?",
      answer: "QuickStart includes three core tiers: Operations (quotations, orders, invoicing, inventory, with optional modules for demand forecasting, CRM, purchasing, and more), Financial Reporting (A/P, A/R, GL reporting with drill-down capabilities), and Budgeting & Forecasting (sales budgeting, scenario modelling, and budget vs actual analysis). All modules integrate directly with your SAP Business One data in real-time."
    },
    {
      question: "How does QuickStart help finance teams using SAP Business One?",
      answer: "Finance teams save hours every week by eliminating manual reporting and spreadsheet reconciliation. QuickStart provides instant access to income statements, balance sheets, and journal entries with full drill-down capability. Month-end close becomes faster and less stressful, audit trails are automatically maintained, and you can analyse your chart of accounts, periods, and budget versions in Excel-friendly formats without complex exports."
    },
    {
      question: "Is QuickStart suitable for SMEs using SAP Business One in the UK?",
      answer: "Yes, QuickStart is specifically designed for UK SMEs using SAP Business One. It's particularly valuable for manufacturing, distribution, and wholesale businesses that need better visibility across operations, finance, and sales. The solution scales with your business-from single-site operations to multi-company groups-and doesn't require a large IT team to manage."
    },
    {
      question: "Do I need a big IT team to run QuickStart?",
      answer: "No. QuickStart is designed for self-service business intelligence, meaning finance, operations, and sales teams can build and adapt dashboards without always relying on IT. The AI-driven interface allows users to ask questions in plain English and get instant visualizations. Your IT team provides governance and security oversight, but day-to-day reporting becomes a business user function."
    },
    {
      question: "Can QuickStart work with our existing SAP Business One add-ons?",
      answer: "Yes. QuickStart integrates with your complete SAP Business One environment, including any extensions or add-ons you're currently using. Whether you run Beas Manufacturing, Produmex WMS, or other SAP B1 solutions, QuickStart can pull data from across your entire SAP ecosystem to provide unified reporting and analytics."
    },
    {
      question: "What's the difference between SAP B1's standard reports and QuickStart?",
      answer: "While SAP Business One includes standard reports, QuickStart delivers interactive, AI-powered dashboards that update in real-time, allow natural language queries, provide predictive analytics, and enable scenario modelling-all without custom development. You get pre-built financial, operational, and planning modules that would take months to build from scratch, plus the flexibility to create your own reports without technical expertise."
    }
  ];

  return (
    <div className="min-h-screen bg-white overflow-x-hidden">
      <SEO 
        title="QuickStart BI for SAP Business One | AI-Powered Analytics & Reporting"
        description="Turn SAP B1 data into decisions fast. Pre-built dashboards, AI-powered insights, real-time reporting. Go live in 2-3 weeks with zero extra data entry."
        keywords="SAP B1 analytics, business intelligence, SAP reporting, AI analytics, QuickStart BI, SAP dashboards"
        canonical="https://www.ascarii.com/quickstart-sap-business-one"
      />

      {/* Hero Section */}
      <section className="relative min-h-[700px] bg-gradient-to-br from-slate-900 via-blue-950 to-slate-900 text-white overflow-hidden">
        <div className="absolute inset-0 opacity-10">
          <div className="absolute inset-0" style={{ backgroundImage: 'linear-gradient(rgba(255, 255, 255, 0.1) 1px, transparent 1px), linear-gradient(90deg, rgba(255, 255, 255, 0.1) 1px, transparent 1px)', backgroundSize: '60px 60px' }} />
        </div>
        
        <div className="container mx-auto px-4 lg:px-8 py-20 relative z-10">
          <div className="max-w-5xl mx-auto">
            <motion.div initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }}>
              <div className="inline-block px-4 py-2 bg-blue-500/20 backdrop-blur-sm border border-blue-400/30 rounded-full text-sm font-semibold mb-6">
                Business Intelligence for SAP Business One
              </div>
              
              <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold mb-6 leading-tight">
                Turn SAP Business One Data into<br />
                <span className="text-sky-300">Decisions You Can Act On-Fast</span>
              </h1>
              
              <p className="text-xl md:text-2xl text-blue-100 mb-8 leading-relaxed">
                QuickStart delivers pre-built dashboards, AI-powered insights, and real-time reporting for your SAP Business One environment. Live in 2-3 weeks, not months.
              </p>
              
              <div className="grid md:grid-cols-3 gap-4 mb-10">
                {[
                  { icon: Clock, title: "Live in 2-3 Weeks", desc: "Proven implementation with pre-configured SAP B1 models" },
                  { icon: Brain, title: "AI-Powered Insights", desc: "Ask questions in plain English, get instant answers" },
                  { icon: Zap, title: "Zero Extra Data Entry", desc: "Direct real-time integration with SAP Business One" }
                ].map((item, i) => (
                  <motion.div 
                    key={i}
                    initial={{ opacity: 0, y: 20 }} 
                    animate={{ opacity: 1, y: 0 }} 
                    transition={{ duration: 0.5, delay: i * 0.1 }}
                    className="flex items-start gap-3 p-4 bg-white/10 backdrop-blur-sm rounded-xl border border-white/20 hover:bg-white/15 transition-colors"
                  >
                    <item.icon className="w-6 h-6 text-cyan-300 flex-shrink-0 mt-0.5" />
                    <div>
                      <h3 className="font-bold text-white mb-1 text-sm">{item.title}</h3>
                      <p className="text-blue-100 text-xs">{item.desc}</p>
                    </div>
                  </motion.div>
                ))}
              </div>

              <div className="flex flex-col sm:flex-row gap-4">
                <a href="/contact" className="inline-block">
                  <button className="w-full sm:w-auto bg-gradient-to-r from-cyan-500 to-blue-600 hover:from-cyan-600 hover:to-blue-700 text-white px-8 py-4 rounded-xl font-semibold text-lg shadow-xl hover:shadow-2xl transition-all flex items-center justify-center gap-2 border-2 border-transparent">
                    <Calendar className="w-5 h-5" />
                    Book a QuickStart Strategy Call
                  </button>
                </a>
                <a href="/contact" className="inline-block">
                  <button className="w-full sm:w-auto bg-white/10 hover:bg-white/20 backdrop-blur-sm border-2 border-white/30 text-white px-8 py-4 rounded-xl font-semibold text-lg transition-all flex items-center justify-center gap-2">
                    <BarChart3 className="w-5 h-5" />
                    Request a Demo
                  </button>
                </a>
              </div>
            </motion.div>
          </div>
        </div>
        
        <div className="absolute bottom-0 left-0 right-0">
          <svg viewBox="0 0 1440 120" fill="none" className="w-full h-16">
            <path d="M0 0L60 10C120 20 240 40 360 46.7C480 53 600 47 720 43.3C840 40 960 40 1080 46.7C1200 53 1320 67 1380 73.3L1440 80V120H1380C1320 120 1200 120 1080 120C960 120 840 120 720 120C600 120 480 120 360 120C240 120 120 120 60 120H0V0Z" fill="white"/>
          </svg>
        </div>
      </section>

      {/* Pain Points Section */}
      <section className="py-20 bg-gradient-to-b from-white to-gray-50">
        <div className="container mx-auto px-4 lg:px-8 max-w-6xl">
          <motion.div 
            initial={{ opacity: 0, y: 20 }} 
            whileInView={{ opacity: 1, y: 0 }} 
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              Why SMEs Using SAP Business One Need QuickStart
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Your SAP Business One system holds all the data you need-but getting meaningful insights shouldn't require hours of manual work, complex exports, or waiting for IT support.
            </p>
          </motion.div>

          <div className="grid md:grid-cols-2 gap-8 mb-12">
            <motion.div 
              initial={{ opacity: 0, x: -20 }} 
              whileInView={{ opacity: 1, x: 0 }} 
              viewport={{ once: true }}
              className="bg-red-50 border-l-4 border-red-600 p-6 rounded-lg"
            >
              <h3 className="text-xl font-bold text-gray-900 mb-4 flex items-center gap-2">
                <AlertTriangle className="w-6 h-6 text-red-600" />
                The Challenge
              </h3>
              <ul className="space-y-3 text-gray-700">
                <li className="flex items-start gap-2">
                  <div className="w-2 h-2 bg-red-600 rounded-full mt-2 flex-shrink-0" />
                  <span><strong>Disconnected reports:</strong> Finance runs one set of reports, operations another, sales something different-no single source of truth</span>
                </li>
                <li className="flex items-start gap-2">
                  <div className="w-2 h-2 bg-red-600 rounded-full mt-2 flex-shrink-0" />
                  <span><strong>Spreadsheet chaos:</strong> Teams spend hours exporting data, reconciling numbers, and updating Excel files manually</span>
                </li>
                <li className="flex items-start gap-2">
                  <div className="w-2 h-2 bg-red-600 rounded-full mt-2 flex-shrink-0" />
                  <span><strong>Slow month-end:</strong> Financial close takes days of manual work, board packs are always rushed</span>
                </li>
                <li className="flex items-start gap-2">
                  <div className="w-2 h-2 bg-red-600 rounded-full mt-2 flex-shrink-0" />
                  <span><strong>Limited visibility:</strong> Can't see real-time stock levels, margin erosion, or cash flow issues until it's too late</span>
                </li>
              </ul>
            </motion.div>

            <motion.div 
              initial={{ opacity: 0, x: 20 }} 
              whileInView={{ opacity: 1, x: 0 }} 
              viewport={{ once: true }}
              className="bg-green-50 border-l-4 border-green-600 p-6 rounded-lg"
            >
              <h3 className="text-xl font-bold text-gray-900 mb-4 flex items-center gap-2">
                <CheckCircle2 className="w-6 h-6 text-green-600" />
                With QuickStart
              </h3>
              <ul className="space-y-3 text-gray-700">
                <li className="flex items-start gap-2">
                  <CheckCircle2 className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" />
                  <span><strong>One trusted view:</strong> Everyone works from the same real-time data-finance, operations, and leadership aligned</span>
                </li>
                <li className="flex items-start gap-2">
                  <CheckCircle2 className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" />
                  <span><strong>Self-service dashboards:</strong> Business users create reports themselves-no more waiting for IT</span>
                </li>
                <li className="flex items-start gap-2">
                  <CheckCircle2 className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" />
                  <span><strong>Faster month-end:</strong> Financial reporting happens in hours with full audit trails built in</span>
                </li>
                <li className="flex items-start gap-2">
                  <CheckCircle2 className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" />
                  <span><strong>Proactive planning:</strong> Spot trends and model scenarios before they impact your business</span>
                </li>
              </ul>
            </motion.div>
          </div>
        </div>
      </section>

      {/* AI Capabilities - MOVED UP */}
      <section className="py-20 bg-gradient-to-br from-indigo-900 via-purple-900 to-blue-900 text-white relative overflow-hidden">
        <div className="absolute inset-0 opacity-10">
          <div className="absolute inset-0" style={{ backgroundImage: 'radial-gradient(circle at 20% 50%, rgba(255, 255, 255, 0.2) 1px, transparent 1px), radial-gradient(circle at 80% 50%, rgba(255, 255, 255, 0.2) 1px, transparent 1px)', backgroundSize: '40px 40px' }} />
        </div>
        
        <div className="container mx-auto px-4 lg:px-8 max-w-6xl relative z-10">
          <motion.div 
            initial={{ opacity: 0, y: 20 }} 
            whileInView={{ opacity: 1, y: 0 }} 
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <div className="inline-block px-4 py-2 bg-purple-500/20 backdrop-blur-sm border border-purple-400/30 rounded-full text-sm font-semibold mb-6">
              Powered by IBM AI
            </div>
            <h2 className="text-3xl md:text-4xl font-bold mb-4">
              AI-Driven Analytics Without the Complexity
            </h2>
            <p className="text-xl text-purple-100 max-w-3xl mx-auto">
              QuickStart brings enterprise AI capabilities to SMEs-making advanced analytics accessible without requiring data science expertise
            </p>
          </motion.div>

          <div className="grid md:grid-cols-2 gap-8 mb-12">
            <motion.div 
              initial={{ opacity: 0, x: -20 }} 
              whileInView={{ opacity: 1, x: 0 }} 
              viewport={{ once: true }}
              className="bg-white/10 backdrop-blur-sm rounded-xl p-6 border border-white/20"
            >
              <div className="flex items-center gap-3 mb-4">
                <Brain className="w-8 h-8 text-purple-300" />
                <h3 className="text-xl font-bold">Natural Language Queries</h3>
              </div>
              <p className="text-purple-100 mb-4">
                Ask questions about your SAP Business One data in plain English and get instant visual answers.
              </p>
              <div className="bg-white/5 rounded-lg p-4 border border-white/10">
                <p className="text-sm text-purple-200 mb-2">Example questions:</p>
                <ul className="space-y-2 text-sm text-purple-100">
                  <li className="flex items-start gap-2">
                    <ArrowRight className="w-4 h-4 flex-shrink-0 mt-0.5" />
                    <span>"Show me top 10 customers by revenue this quarter"</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <ArrowRight className="w-4 h-4 flex-shrink-0 mt-0.5" />
                    <span>"Which products have the highest margin?"</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <ArrowRight className="w-4 h-4 flex-shrink-0 mt-0.5" />
                    <span>"Compare this month's sales to last year"</span>
                  </li>
                </ul>
              </div>
            </motion.div>

            <motion.div 
              initial={{ opacity: 0, x: 20 }} 
              whileInView={{ opacity: 1, x: 0 }} 
              viewport={{ once: true }}
              className="bg-white/10 backdrop-blur-sm rounded-xl p-6 border border-white/20"
            >
              <div className="flex items-center gap-3 mb-4">
                <Sparkles className="w-8 h-8 text-purple-300" />
                <h3 className="text-xl font-bold">Automatic Insights</h3>
              </div>
              <p className="text-purple-100 mb-4">
                AI suggests relevant visualizations, highlights anomalies, and surfaces patterns you might miss.
              </p>
              <ul className="space-y-3 text-purple-100">
                <li className="flex items-start gap-2">
                  <CheckCircle2 className="w-5 h-5 text-purple-300 flex-shrink-0 mt-0.5" />
                  <span>Automatically detects trends, spikes, and unusual patterns</span>
                </li>
                <li className="flex items-start gap-2">
                  <CheckCircle2 className="w-5 h-5 text-purple-300 flex-shrink-0 mt-0.5" />
                  <span>Recommends the best chart types for your data</span>
                </li>
                <li className="flex items-start gap-2">
                  <CheckCircle2 className="w-5 h-5 text-purple-300 flex-shrink-0 mt-0.5" />
                  <span>Alerts you to significant changes requiring attention</span>
                </li>
              </ul>
            </motion.div>

            <motion.div 
              initial={{ opacity: 0, x: -20 }} 
              whileInView={{ opacity: 1, x: 0 }} 
              viewport={{ once: true }}
              className="bg-white/10 backdrop-blur-sm rounded-xl p-6 border border-white/20"
            >
              <div className="flex items-center gap-3 mb-4">
                <TrendingUp className="w-8 h-8 text-purple-300" />
                <h3 className="text-xl font-bold">Predictive Analytics</h3>
              </div>
              <p className="text-purple-100 mb-4">
                Forecast future performance based on historical patterns and current trends in your SAP B1 data.
              </p>
              <ul className="space-y-3 text-purple-100">
                <li className="flex items-start gap-2">
                  <CheckCircle2 className="w-5 h-5 text-purple-300 flex-shrink-0 mt-0.5" />
                  <span>Predict cash flow requirements for next quarter</span>
                </li>
                <li className="flex items-start gap-2">
                  <CheckCircle2 className="w-5 h-5 text-purple-300 flex-shrink-0 mt-0.5" />
                  <span>Forecast inventory needs based on seasonal trends</span>
                </li>
                <li className="flex items-start gap-2">
                  <CheckCircle2 className="w-5 h-5 text-purple-300 flex-shrink-0 mt-0.5" />
                  <span>Identify customers at risk of churn before they leave</span>
                </li>
              </ul>
            </motion.div>

            <motion.div 
              initial={{ opacity: 0, x: 20 }} 
              whileInView={{ opacity: 1, x: 0 }} 
              viewport={{ once: true }}
              className="bg-white/10 backdrop-blur-sm rounded-xl p-6 border border-white/20"
            >
              <div className="flex items-center gap-3 mb-4">
                <ShieldCheck className="w-8 h-8 text-purple-300" />
                <h3 className="text-xl font-bold">Secure & Governed</h3>
              </div>
              <p className="text-purple-100 mb-4">
                Enterprise-grade security and governance built on IBM's proven analytics platform.
              </p>
              <ul className="space-y-3 text-purple-100">
                <li className="flex items-start gap-2">
                  <CheckCircle2 className="w-5 h-5 text-purple-300 flex-shrink-0 mt-0.5" />
                  <span>Your data stays secure within your SAP B1 environment</span>
                </li>
                <li className="flex items-start gap-2">
                  <CheckCircle2 className="w-5 h-5 text-purple-300 flex-shrink-0 mt-0.5" />
                  <span>Role-based access controls and complete audit trails</span>
                </li>
              </ul>
            </motion.div>
          </div>

          <motion.div 
            initial={{ opacity: 0, y: 20 }} 
            whileInView={{ opacity: 1, y: 0 }} 
            viewport={{ once: true }}
            className="bg-gradient-to-r from-purple-500/20 to-blue-500/20 backdrop-blur-sm border border-purple-400/30 rounded-xl p-8 text-center"
          >
            <Lightbulb className="w-12 h-12 text-yellow-300 mx-auto mb-4" />
            <h3 className="text-2xl font-bold mb-3">The Key Difference</h3>
            <p className="text-lg text-purple-100 max-w-3xl mx-auto">
              While other BI tools require months of configuration, QuickStart's AI understands SAP Business One natively. You get sophisticated analytics that "just work" from day one-no data science degree required.
            </p>
          </motion.div>
        </div>
      </section>

      {/* How It Works with Image */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-4 lg:px-8 max-w-6xl">
          <motion.div 
            initial={{ opacity: 0, y: 20 }} 
            whileInView={{ opacity: 1, y: 0 }} 
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              Three Powerful Intelligence Layers
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              QuickStart transforms your SAP Business One data into actionable insights across operations, finance, and planning
            </p>
          </motion.div>

          {/* Operations with Image */}
          <div className="grid md:grid-cols-2 gap-12 items-stretch mb-16">
            <motion.div 
              initial={{ opacity: 0, x: -30 }} 
              whileInView={{ opacity: 1, x: 0 }} 
              viewport={{ once: true }}
              className="flex"
            >
              <img 
                src="https://images.unsplash.com/photo-1644079446600-219068676743?w=800&q=80" 
                alt="Warehouse operations with real-time inventory visibility" 
                className="rounded-2xl shadow-2xl w-full h-full object-cover"
              />
            </motion.div>
            
            <motion.div 
              initial={{ opacity: 0, x: 30 }} 
              whileInView={{ opacity: 1, x: 0 }} 
              viewport={{ once: true }}
              className="bg-gradient-to-br from-blue-900 to-cyan-900 text-white rounded-2xl p-8 flex flex-col"
            >
              <div className="flex items-center gap-3 mb-4">
                <div className="w-12 h-12 bg-cyan-400 rounded-lg flex items-center justify-center">
                  <Boxes className="w-6 h-6 text-blue-900" />
                </div>
                <h3 className="text-2xl font-bold">Operations Intelligence</h3>
              </div>
              <p className="text-blue-100 mb-4">Real-time visibility into quotations, orders, invoicing, and inventory-with zero additional data entry.</p>
              <ul className="space-y-2 text-blue-100">
                <li className="flex items-start gap-2">
                  <CheckCircle2 className="w-4 h-4 text-cyan-300 flex-shrink-0 mt-1" />
                  <span>Track quote conversion and pipeline value</span>
                </li>
                <li className="flex items-start gap-2">
                  <CheckCircle2 className="w-4 h-4 text-cyan-300 flex-shrink-0 mt-1" />
                  <span>Monitor order backlog and delivery performance</span>
                </li>
                <li className="flex items-start gap-2">
                  <CheckCircle2 className="w-4 h-4 text-cyan-300 flex-shrink-0 mt-1" />
                  <span>Analyze stock levels and identify slow-moving items</span>
                </li>
                <li className="flex items-start gap-2">
                  <CheckCircle2 className="w-4 h-4 text-cyan-300 flex-shrink-0 mt-1" />
                  <span>Optional: Demand forecasting, CRM, purchasing analytics</span>
                </li>
              </ul>
            </motion.div>
          </div>

          {/* Financial with Image - Reversed */}
          <div className="grid md:grid-cols-2 gap-12 items-stretch mb-16">
            <motion.div 
              initial={{ opacity: 0, x: -30 }} 
              whileInView={{ opacity: 1, x: 0 }} 
              viewport={{ once: true }}
              className="bg-gradient-to-br from-blue-800 to-cyan-900 text-white rounded-2xl p-8 order-2 md:order-1 flex flex-col"
            >
              <div className="flex items-center gap-3 mb-4">
                <div className="w-12 h-12 bg-cyan-300 rounded-lg flex items-center justify-center">
                  <FileText className="w-6 h-6 text-blue-900" />
                </div>
                <h3 className="text-2xl font-bold">Financial Reporting</h3>
              </div>
              <p className="text-blue-100 mb-4">Transform your month-end close from days of stress to hours of precision.</p>
              <ul className="space-y-2 text-blue-100">
                <li className="flex items-start gap-2">
                  <CheckCircle2 className="w-4 h-4 text-cyan-300 flex-shrink-0 mt-1" />
                  <span>Interactive P&L and balance sheet with drill-down</span>
                </li>
                <li className="flex items-start gap-2">
                  <CheckCircle2 className="w-4 h-4 text-cyan-300 flex-shrink-0 mt-1" />
                  <span>A/P and A/R analysis with payment forecasting</span>
                </li>
                <li className="flex items-start gap-2">
                  <CheckCircle2 className="w-4 h-4 text-cyan-300 flex-shrink-0 mt-1" />
                  <span>Audit-ready trails and variance analysis</span>
                </li>
                <li className="flex items-start gap-2">
                  <CheckCircle2 className="w-4 h-4 text-cyan-300 flex-shrink-0 mt-1" />
                  <span>Excel-friendly chart of accounts access</span>
                </li>
              </ul>
            </motion.div>

            <motion.div 
              initial={{ opacity: 0, x: 30 }} 
              whileInView={{ opacity: 1, x: 0 }} 
              viewport={{ once: true }}
              className="order-1 md:order-2 flex"
            >
              <img 
                src="https://images.pexels.com/photos/7821672/pexels-photo-7821672.jpeg?w=800&q=80" 
                alt="Finance team analyzing reports and financial data" 
                className="rounded-2xl shadow-2xl w-full h-full object-cover"
              />
            </motion.div>
          </div>

          {/* Budgeting with Image */}
          <div className="grid md:grid-cols-2 gap-12 items-stretch">
            <motion.div 
              initial={{ opacity: 0, x: -30 }} 
              whileInView={{ opacity: 1, x: 0 }} 
              viewport={{ once: true }}
              className="flex"
            >
              <img 
                src="https://images.pexels.com/photos/3184292/pexels-photo-3184292.jpeg?w=800&q=80" 
                alt="Strategic planning and business decision making" 
                className="rounded-2xl shadow-2xl w-full h-full object-cover"
              />
            </motion.div>

            <motion.div 
              initial={{ opacity: 0, x: 30 }} 
              whileInView={{ opacity: 1, x: 0 }} 
              viewport={{ once: true }}
              className="bg-gradient-to-br from-blue-900 to-cyan-900 text-white rounded-2xl p-8 flex flex-col"
            >
              <div className="flex items-center gap-3 mb-4">
                <div className="w-12 h-12 bg-cyan-300 rounded-lg flex items-center justify-center">
                  <Calculator className="w-6 h-6 text-blue-900" />
                </div>
                <h3 className="text-2xl font-bold">Budgeting & Forecasting</h3>
              </div>
              <p className="text-blue-100 mb-4">Plan ahead with confidence using scenario modelling and budget vs actual analysis.</p>
              <ul className="space-y-2 text-blue-100">
                <li className="flex items-start gap-2">
                  <CheckCircle2 className="w-4 h-4 text-cyan-300 flex-shrink-0 mt-1" />
                  <span>Flexible sales budgeting by customer, product, region</span>
                </li>
                <li className="flex items-start gap-2">
                  <CheckCircle2 className="w-4 h-4 text-cyan-300 flex-shrink-0 mt-1" />
                  <span>Real-time budget vs actual comparisons</span>
                </li>
                <li className="flex items-start gap-2">
                  <CheckCircle2 className="w-4 h-4 text-cyan-300 flex-shrink-0 mt-1" />
                  <span>What-if scenario testing before committing resources</span>
                </li>
                <li className="flex items-start gap-2">
                  <CheckCircle2 className="w-4 h-4 text-cyan-300 flex-shrink-0 mt-1" />
                  <span>Rolling forecasts updated continuously</span>
                </li>
              </ul>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Benefits by Persona with Visual Cards */}
      <section className="py-20 bg-gradient-to-b from-gray-900 via-slate-900 to-gray-900 text-white relative overflow-hidden">
        <div className="absolute inset-0 opacity-5">
          <div className="absolute inset-0" style={{ backgroundImage: 'linear-gradient(rgba(255, 255, 255, 0.1) 1px, transparent 1px), linear-gradient(90deg, rgba(255, 255, 255, 0.1) 1px, transparent 1px)', backgroundSize: '60px 60px' }} />
        </div>

        <div className="container mx-auto px-4 lg:px-8 max-w-6xl relative z-10">
          <motion.div 
            initial={{ opacity: 0, y: 20 }} 
            whileInView={{ opacity: 1, y: 0 }} 
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <h2 className="text-3xl md:text-4xl font-bold mb-4">
              How QuickStart Helps Your Team
            </h2>
            <p className="text-xl text-gray-300 max-w-3xl mx-auto">
              Every role benefits from better visibility into SAP Business One data
            </p>
          </motion.div>

          <div className="grid md:grid-cols-2 gap-8">
            <motion.div 
              initial={{ opacity: 0, y: 20 }} 
              whileInView={{ opacity: 1, y: 0 }} 
              viewport={{ once: true }}
              className="bg-gradient-to-br from-blue-600 to-cyan-700 rounded-xl p-6 border border-blue-400/30"
            >
              <div className="flex items-center gap-3 mb-4">
                <div className="w-12 h-12 bg-white/20 rounded-lg flex items-center justify-center">
                  <DollarSign className="w-6 h-6 text-white" />
                </div>
                <h3 className="text-xl font-bold">Finance Teams</h3>
              </div>
              <ul className="space-y-3 text-blue-50">
                <li className="flex items-start gap-2">
                  <CheckCircle2 className="w-5 h-5 text-cyan-200 flex-shrink-0 mt-0.5" />
                  <span>Eliminate hours of manual Excel work every week</span>
                </li>
                <li className="flex items-start gap-2">
                  <CheckCircle2 className="w-5 h-5 text-cyan-200 flex-shrink-0 mt-0.5" />
                  <span>Close books in hours instead of days</span>
                </li>
                <li className="flex items-start gap-2">
                  <CheckCircle2 className="w-5 h-5 text-cyan-200 flex-shrink-0 mt-0.5" />
                  <span>Drill down to transaction level instantly</span>
                </li>
                <li className="flex items-start gap-2">
                  <CheckCircle2 className="w-5 h-5 text-cyan-200 flex-shrink-0 mt-0.5" />
                  <span>Spot margin erosion and cash flow issues early</span>
                </li>
              </ul>
            </motion.div>

            <motion.div 
              initial={{ opacity: 0, y: 20 }} 
              whileInView={{ opacity: 1, y: 0 }} 
              viewport={{ once: true }}
              className="bg-gradient-to-br from-blue-600 to-cyan-700 rounded-xl p-6 border border-blue-400/30"
            >
              <div className="flex items-center gap-3 mb-4">
                <div className="w-12 h-12 bg-white/20 rounded-lg flex items-center justify-center">
                  <Warehouse className="w-6 h-6 text-white" />
                </div>
                <h3 className="text-xl font-bold">Operations Teams</h3>
              </div>
              <ul className="space-y-3 text-blue-50">
                <li className="flex items-start gap-2">
                  <CheckCircle2 className="w-5 h-5 text-cyan-200 flex-shrink-0 mt-0.5" />
                  <span>See real-time stock levels and order status</span>
                </li>
                <li className="flex items-start gap-2">
                  <CheckCircle2 className="w-5 h-5 text-cyan-200 flex-shrink-0 mt-0.5" />
                  <span>Identify bottlenecks before customers complain</span>
                </li>
                <li className="flex items-start gap-2">
                  <CheckCircle2 className="w-5 h-5 text-cyan-200 flex-shrink-0 mt-0.5" />
                  <span>Optimize inventory-reduce excess without stockouts</span>
                </li>
                <li className="flex items-start gap-2">
                  <CheckCircle2 className="w-5 h-5 text-cyan-200 flex-shrink-0 mt-0.5" />
                  <span>Use demand forecasting to plan proactively</span>
                </li>
              </ul>
            </motion.div>

            <motion.div 
              initial={{ opacity: 0, y: 20 }} 
              whileInView={{ opacity: 1, y: 0 }} 
              viewport={{ once: true }}
              className="bg-gradient-to-br from-blue-600 to-cyan-700 rounded-xl p-6 border border-blue-400/30"
            >
              <div className="flex items-center gap-3 mb-4">
                <div className="w-12 h-12 bg-white/20 rounded-lg flex items-center justify-center">
                  <Target className="w-6 h-6 text-white" />
                </div>
                <h3 className="text-xl font-bold">Leadership</h3>
              </div>
              <ul className="space-y-3 text-blue-50">
                <li className="flex items-start gap-2">
                  <CheckCircle2 className="w-5 h-5 text-cyan-200 flex-shrink-0 mt-0.5" />
                  <span>Make decisions based on complete, current data</span>
                </li>
                <li className="flex items-start gap-2">
                  <CheckCircle2 className="w-5 h-5 text-cyan-200 flex-shrink-0 mt-0.5" />
                  <span>See how finance, operations, and sales interconnect</span>
                </li>
                <li className="flex items-start gap-2">
                  <CheckCircle2 className="w-5 h-5 text-cyan-200 flex-shrink-0 mt-0.5" />
                  <span>Model future scenarios before committing</span>
                </li>
                <li className="flex items-start gap-2">
                  <CheckCircle2 className="w-5 h-5 text-cyan-200 flex-shrink-0 mt-0.5" />
                  <span>Focus on strategy, not chasing data</span>
                </li>
              </ul>
            </motion.div>

            <motion.div 
              initial={{ opacity: 0, y: 20 }} 
              whileInView={{ opacity: 1, y: 0 }} 
              viewport={{ once: true }}
              className="bg-gradient-to-br from-blue-600 to-cyan-700 rounded-xl p-6 border border-blue-400/30"
            >
              <div className="flex items-center gap-3 mb-4">
                <div className="w-12 h-12 bg-white/20 rounded-lg flex items-center justify-center">
                  <Database className="w-6 h-6 text-white" />
                </div>
                <h3 className="text-xl font-bold">IT Teams</h3>
              </div>
              <ul className="space-y-3 text-cyan-50">
                <li className="flex items-start gap-2">
                  <CheckCircle2 className="w-5 h-5 text-cyan-200 flex-shrink-0 mt-0.5" />
                  <span>Reduce custom report requests dramatically</span>
                </li>
                <li className="flex items-start gap-2">
                  <CheckCircle2 className="w-5 h-5 text-cyan-200 flex-shrink-0 mt-0.5" />
                  <span>Enterprise security with IBM analytics platform</span>
                </li>
                <li className="flex items-start gap-2">
                  <CheckCircle2 className="w-5 h-5 text-cyan-200 flex-shrink-0 mt-0.5" />
                  <span>Pre-built SAP B1 integration-no custom code</span>
                </li>
                <li className="flex items-start gap-2">
                  <CheckCircle2 className="w-5 h-5 text-cyan-200 flex-shrink-0 mt-0.5" />
                  <span>Scalable cloud architecture that grows with you</span>
                </li>
              </ul>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Implementation Timeline with Visual */}
      <section className="py-20 bg-gradient-to-b from-white to-gray-50">
        <div className="container mx-auto px-4 lg:px-8 max-w-6xl">
          <motion.div 
            initial={{ opacity: 0, y: 20 }} 
            whileInView={{ opacity: 1, y: 0 }} 
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              Live in 2-3 Weeks
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Our proven three-step implementation delivers QuickStart quickly without disrupting your business
            </p>
          </motion.div>

          <div className="grid md:grid-cols-3 gap-8">
            <motion.div 
              initial={{ opacity: 0, y: 20 }} 
              whileInView={{ opacity: 1, y: 0 }} 
              viewport={{ once: true }}
              className="bg-gradient-to-br from-blue-600 to-cyan-600 text-white rounded-xl p-6 shadow-xl"
            >
              <div className="flex items-center gap-3 mb-4">
                <div className="w-12 h-12 bg-white text-blue-600 rounded-full flex items-center justify-center font-bold text-xl">
                  1
                </div>
                <h3 className="text-xl font-bold">Discovery</h3>
              </div>
              <p className="text-blue-100 mb-3 text-sm"><strong>2-3 days</strong></p>
              <ul className="space-y-2 text-blue-50 text-sm">
                <li className="flex items-start gap-2">
                  <CheckCircle2 className="w-4 h-4 flex-shrink-0 mt-0.5" />
                  <span>Review SAP B1 environment</span>
                </li>
                <li className="flex items-start gap-2">
                  <CheckCircle2 className="w-4 h-4 flex-shrink-0 mt-0.5" />
                  <span>Define reporting priorities</span>
                </li>
                <li className="flex items-start gap-2">
                  <CheckCircle2 className="w-4 h-4 flex-shrink-0 mt-0.5" />
                  <span>Agree implementation plan</span>
                </li>
              </ul>
            </motion.div>

            <motion.div 
              initial={{ opacity: 0, y: 20 }} 
              whileInView={{ opacity: 1, y: 0 }} 
              viewport={{ once: true }}
              transition={{ delay: 0.1 }}
              className="bg-gradient-to-br from-blue-600 to-cyan-600 text-white rounded-xl p-6 shadow-xl"
            >
              <div className="flex items-center gap-3 mb-4">
                <div className="w-12 h-12 bg-white text-blue-600 rounded-full flex items-center justify-center font-bold text-xl">
                  2
                </div>
                <h3 className="text-xl font-bold">Configuration</h3>
              </div>
              <p className="text-blue-100 mb-3 text-sm"><strong>1-2 weeks</strong></p>
              <ul className="space-y-2 text-blue-50 text-sm">
                <li className="flex items-start gap-2">
                  <CheckCircle2 className="w-4 h-4 flex-shrink-0 mt-0.5" />
                  <span>Connect to SAP Business One</span>
                </li>
                <li className="flex items-start gap-2">
                  <CheckCircle2 className="w-4 h-4 flex-shrink-0 mt-0.5" />
                  <span>Configure dashboards with your data</span>
                </li>
                <li className="flex items-start gap-2">
                  <CheckCircle2 className="w-4 h-4 flex-shrink-0 mt-0.5" />
                  <span>Validate accuracy and reconciliation</span>
                </li>
              </ul>
            </motion.div>

            <motion.div 
              initial={{ opacity: 0, y: 20 }} 
              whileInView={{ opacity: 1, y: 0 }} 
              viewport={{ once: true }}
              transition={{ delay: 0.2 }}
              className="bg-gradient-to-br from-blue-600 to-cyan-600 text-white rounded-xl p-6 shadow-xl"
            >
              <div className="flex items-center gap-3 mb-4">
                <div className="w-12 h-12 bg-white text-blue-600 rounded-full flex items-center justify-center font-bold text-xl">
                  3
                </div>
                <h3 className="text-xl font-bold">Go-Live</h3>
              </div>
              <p className="text-blue-100 mb-3 text-sm"><strong>2-3 days</strong></p>
              <ul className="space-y-2 text-blue-50 text-sm">
                <li className="flex items-start gap-2">
                  <CheckCircle2 className="w-4 h-4 flex-shrink-0 mt-0.5" />
                  <span>User training on dashboards and AI</span>
                </li>
                <li className="flex items-start gap-2">
                  <CheckCircle2 className="w-4 h-4 flex-shrink-0 mt-0.5" />
                  <span>Self-service reporting handover</span>
                </li>
                <li className="flex items-start gap-2">
                  <CheckCircle2 className="w-4 h-4 flex-shrink-0 mt-0.5" />
                  <span>Ongoing support and optimization</span>
                </li>
              </ul>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Why Ascarii with Image */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-4 lg:px-8 max-w-6xl">
          <div className="grid md:grid-cols-2 gap-12 items-center">
            <motion.div 
              initial={{ opacity: 0, x: -30 }} 
              whileInView={{ opacity: 1, x: 0 }} 
              viewport={{ once: true }}
            >
              <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-6">
                Why Choose Ascarii for QuickStart
              </h2>
              <p className="text-lg text-gray-600 mb-6">
                Bringing QuickStart to UK & Ireland SMEs through proven SAP Business One expertise and strategic partnership with NewIntelligence.
              </p>
              
              <div className="space-y-4">
                <div className="flex items-start gap-3">
                  <Award className="w-6 h-6 text-blue-600 flex-shrink-0 mt-1" />
                  <div>
                    <h4 className="font-bold text-gray-900 mb-1">SAP Premier Partner</h4>
                    <p className="text-gray-600 text-sm">Deep SAP Business One expertise serving UK SMEs-we understand your environment and industry</p>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <Users className="w-6 h-6 text-blue-600 flex-shrink-0 mt-1" />
                  <div>
                    <h4 className="font-bold text-gray-900 mb-1">Proven Implementation</h4>
                    <p className="text-gray-600 text-sm">Refined methodology delivers QuickStart in 2-3 weeks with minimal disruption</p>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <RefreshCw className="w-6 h-6 text-purple-600 flex-shrink-0 mt-1" />
                  <div>
                    <h4 className="font-bold text-gray-900 mb-1">Ongoing Optimization</h4>
                    <p className="text-gray-600 text-sm">We help you continuously improve analytics as your business evolves</p>
                  </div>
                </div>
              </div>

              <div className="mt-8">
                <a href="/contact">
                  <button className="bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700 text-white px-8 py-4 rounded-xl font-semibold shadow-lg hover:shadow-xl transition-all flex items-center gap-2">
                    <Users className="w-5 h-5" />
                    Talk to a SAP B1 Specialist
                  </button>
                </a>
              </div>
            </motion.div>

            <motion.div 
              initial={{ opacity: 0, x: 30 }} 
              whileInView={{ opacity: 1, x: 0 }} 
              viewport={{ once: true }}
            >
              <img 
                src="https://images.unsplash.com/photo-1551288049-bebda4e38f71?w=800&q=80" 
                alt="Business analytics dashboard showing SAP Business One insights" 
                className="rounded-2xl shadow-2xl w-full h-auto"
              />
            </motion.div>
          </div>
        </div>
      </section>

      {/* Customer Stories with Purple Gradient Boxes */}
      <section className="py-20 bg-gradient-to-br from-slate-900 via-purple-900 to-indigo-900 text-white relative overflow-hidden">
        <div className="absolute inset-0 opacity-5">
          <div className="absolute inset-0" style={{ backgroundImage: 'radial-gradient(circle, rgba(255, 255, 255, 0.1) 1px, transparent 1px)', backgroundSize: '40px 40px' }} />
        </div>

        <div className="container mx-auto px-4 lg:px-8 max-w-6xl relative z-10">
          <motion.div 
            initial={{ opacity: 0, y: 20 }} 
            whileInView={{ opacity: 1, y: 0 }} 
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <h2 className="text-3xl md:text-4xl font-bold mb-4">
              Real Results for SAP Business One Customers
            </h2>
            <p className="text-xl text-purple-200 max-w-3xl mx-auto">
              SMEs using QuickStart are making faster decisions and running more profitable operations
            </p>
          </motion.div>

          <div className="grid md:grid-cols-2 gap-8">
            <motion.div 
              initial={{ opacity: 0, y: 20 }} 
              whileInView={{ opacity: 1, y: 0 }} 
              viewport={{ once: true }}
              className="bg-white/10 backdrop-blur-sm rounded-xl p-6 border border-white/20"
            >
              <div className="flex items-center gap-3 mb-4">
                <div className="w-12 h-12 bg-cyan-400 rounded-full flex items-center justify-center">
                  <TruckIcon className="w-6 h-6 text-cyan-900" />
                </div>
                <div>
                  <h3 className="font-bold">UK Distributor</h3>
                  <p className="text-sm text-purple-200">Wholesale, £15M Revenue</p>
                </div>
              </div>
              <p className="text-purple-100 mb-4 italic">
                "Month-end used to take 5-6 days. With QuickStart, we close in less than 2 days-and get better insights. Our team isn't burnt out every month."
              </p>
              <div className="border-t border-white/20 pt-4">
                <p className="text-sm text-purple-200"><strong>Key benefits:</strong> Faster close, reduced manual work, better board reporting</p>
              </div>
            </motion.div>

            <motion.div 
              initial={{ opacity: 0, y: 20 }} 
              whileInView={{ opacity: 1, y: 0 }} 
              viewport={{ once: true }}
              className="bg-white/10 backdrop-blur-sm rounded-xl p-6 border border-white/20"
            >
              <div className="flex items-center gap-3 mb-4">
                <div className="w-12 h-12 bg-cyan-400 rounded-full flex items-center justify-center">
                  <Boxes className="w-6 h-6 text-cyan-900" />
                </div>
                <div>
                  <h3 className="font-bold">Manufacturing SME</h3>
                  <p className="text-sm text-purple-200">45 Employees</p>
                </div>
              </div>
              <p className="text-purple-100 mb-4 italic">
                "We were flying blind on margins. QuickStart showed which product lines were profitable. We've improved gross margin by 4 percentage points in six months."
              </p>
              <div className="border-t border-white/20 pt-4">
                <p className="text-sm text-purple-200"><strong>Key benefits:</strong> Margin visibility, profitability analysis, strategic focus</p>
              </div>
            </motion.div>

            <motion.div 
              initial={{ opacity: 0, y: 20 }} 
              whileInView={{ opacity: 1, y: 0 }} 
              viewport={{ once: true }}
              className="bg-white/10 backdrop-blur-sm rounded-xl p-6 border border-white/20"
            >
              <div className="flex items-center gap-3 mb-4">
                <div className="w-12 h-12 bg-cyan-400 rounded-full flex items-center justify-center">
                  <Warehouse className="w-6 h-6 text-cyan-900" />
                </div>
                <div>
                  <h3 className="font-bold">Multi-Site Wholesaler</h3>
                  <p className="text-sm text-purple-200">3 Warehouses, £22M</p>
                </div>
              </div>
              <p className="text-purple-100 mb-4 italic">
                "Stock visibility was our biggest headache. Now we see real-time levels across all sites, identify slow movers instantly, and working capital has improved significantly."
              </p>
              <div className="border-t border-white/20 pt-4">
                <p className="text-sm text-purple-200"><strong>Key benefits:</strong> Multi-site visibility, inventory optimization, better cash flow</p>
              </div>
            </motion.div>

            <motion.div 
              initial={{ opacity: 0, y: 20 }} 
              whileInView={{ opacity: 1, y: 0 }} 
              viewport={{ once: true }}
              className="bg-white/10 backdrop-blur-sm rounded-xl p-6 border border-white/20"
            >
              <div className="flex items-center gap-3 mb-4">
                <div className="w-12 h-12 bg-cyan-400 rounded-full flex items-center justify-center">
                  <TrendingUp className="w-6 h-6 text-cyan-900" />
                </div>
                <div>
                  <h3 className="font-bold">Professional Services</h3>
                  <p className="text-sm text-purple-200">£8M Revenue</p>
                </div>
              </div>
              <p className="text-purple-100 mb-4 italic">
                "Our finance team spent more time creating reports than analyzing them. QuickStart freed up hours every week-now they help the business make better decisions."
              </p>
              <div className="border-t border-white/20 pt-4">
                <p className="text-sm text-purple-200"><strong>Key benefits:</strong> Time savings, proactive analysis, strategic contribution</p>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="py-20 bg-gradient-to-b from-gray-50 to-white">
        <div className="container mx-auto px-4 lg:px-8 max-w-4xl">
          <motion.div 
            initial={{ opacity: 0, y: 20 }} 
            whileInView={{ opacity: 1, y: 0 }} 
            viewport={{ once: true }}
            className="text-center mb-12"
          >
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              Frequently Asked Questions
            </h2>
            <p className="text-xl text-gray-600">
              Everything you need to know about QuickStart for SAP Business One
            </p>
          </motion.div>

          <div className="space-y-4">
            {faqs.map((faq, index) => (
              <motion.div 
                key={index}
                initial={{ opacity: 0, y: 10 }} 
                whileInView={{ opacity: 1, y: 0 }} 
                viewport={{ once: true }}
                transition={{ delay: index * 0.05 }}
                className="bg-white rounded-lg border border-gray-200 overflow-hidden hover:shadow-lg transition-shadow"
              >
                <button
                  onClick={() => toggleFaq(index)}
                  className="w-full flex items-center justify-between p-6 text-left hover:bg-gray-50 transition-colors"
                >
                  <span className="font-semibold text-gray-900 pr-4">{faq.question}</span>
                  {expandedFaq === index ? (
                    <ChevronUp className="w-5 h-5 text-blue-600 flex-shrink-0" />
                  ) : (
                    <ChevronDown className="w-5 h-5 text-gray-400 flex-shrink-0" />
                  )}
                </button>
                {expandedFaq === index && (
                  <div className="px-6 pb-6">
                    <p className="text-gray-700 leading-relaxed">{faq.answer}</p>
                  </div>
                )}
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section className="py-20 bg-gradient-to-br from-indigo-900 via-purple-900 to-blue-900 text-white relative overflow-hidden">
        <div className="absolute inset-0 opacity-10">
          <div className="absolute inset-0" style={{ backgroundImage: 'radial-gradient(circle at 30% 50%, rgba(255, 255, 255, 0.2) 1px, transparent 1px), radial-gradient(circle at 70% 50%, rgba(255, 255, 255, 0.2) 1px, transparent 1px)', backgroundSize: '50px 50px' }} />
        </div>
        
        <div className="container mx-auto px-4 lg:px-8 max-w-4xl relative z-10">
          <motion.div 
            initial={{ opacity: 0, y: 20 }} 
            whileInView={{ opacity: 1, y: 0 }} 
            viewport={{ once: true }}
            className="text-center"
          >
            <h2 className="text-3xl md:text-4xl font-bold mb-6">
              Turn Your SAP Business One Data into Your Competitive Advantage
            </h2>
            <p className="text-xl text-purple-100 mb-8 max-w-2xl mx-auto">
              Stop wrestling with spreadsheets and manual reports. Start making confident, data-driven decisions using the SAP Business One data you already have-live in 2-3 weeks.
            </p>

            <div className="flex flex-col sm:flex-row gap-4 justify-center mb-8">
              <a href="/contact" className="inline-block">
                <button className="w-full sm:w-auto bg-white text-purple-900 hover:bg-purple-50 px-8 py-4 rounded-xl font-semibold text-lg shadow-xl hover:shadow-2xl transition-all flex items-center justify-center gap-2 border-2 border-transparent">
                  <Users className="w-5 h-5" />
                  Book a QuickStart Strategy Call
                </button>
              </a>
              <a href="/contact" className="inline-block">
                <button className="w-full sm:w-auto bg-cyan-500/20 hover:bg-cyan-500/30 backdrop-blur-sm border-2 border-white/30 text-white px-8 py-4 rounded-xl font-semibold text-lg transition-all flex items-center justify-center gap-2">
                  <BarChart3 className="w-5 h-5" />
                  Request a Demo
                </button>
              </a>
            </div>

            <div className="bg-white/10 backdrop-blur-sm rounded-xl p-6 border border-white/20 max-w-2xl mx-auto">
              <h3 className="font-bold text-white mb-3">What Happens Next?</h3>
              <div className="grid md:grid-cols-3 gap-4 text-left">
                <div className="flex items-start gap-2">
                  <div className="w-6 h-6 bg-cyan-400 rounded-full flex items-center justify-center flex-shrink-0 text-purple-900 font-bold text-sm mt-0.5">1</div>
                  <div>
                    <p className="text-sm font-semibold">Discovery Call</p>
                    <p className="text-xs text-purple-200">Discuss your SAP B1 environment</p>
                  </div>
                </div>
                <div className="flex items-start gap-2">
                  <div className="w-6 h-6 bg-cyan-400 rounded-full flex items-center justify-center flex-shrink-0 text-purple-900 font-bold text-sm mt-0.5">2</div>
                  <div>
                    <p className="text-sm font-semibold">Tailored Demo</p>
                    <p className="text-xs text-purple-200">See QuickStart with your data</p>
                  </div>
                </div>
                <div className="flex items-start gap-2">
                  <div className="w-6 h-6 bg-cyan-400 rounded-full flex items-center justify-center flex-shrink-0 text-purple-900 font-bold text-sm mt-0.5">3</div>
                  <div>
                    <p className="text-sm font-semibold">Implementation Plan</p>
                    <p className="text-xs text-purple-200">Clear timeline and next steps</p>
                  </div>
                </div>
              </div>
            </div>

            <p className="text-sm text-purple-200 mt-6">
              No obligation-just a conversation about how QuickStart can help your business make better decisions, faster.
            </p>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default QuickStartPage;
